import {
  ChangeDetectorRef,
  Component,
  Input,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import type { AbstractControl } from '@angular/forms';
import {
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { noWhitespaceValidator, NUMBER_WITH_SEPARATORS_PATTERN } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { trimmedFormValue } from '@core/integrations-util';
import type { LocationBrief } from '@core/locations-types';
import type { SelectOption } from '@core/user-interface-legacy';
import {
  CheckboxComponent,
  DividerComponent,
  FormFieldComponent,
  HeadingComponent,
  IconHelpComponent,
  InputComponent,
  LabelComponent,
  RtTooltip,
  SelectComponent,
  SpinnerComponent,
  TextareaComponent,
} from '@core/user-interface-legacy';

import { Destroyable, takeUntilDestroyed } from '../../../common';
import {
  AdminSelectInput,
  AdminSelectInputComponent,
} from '../../../dynamic/inputs/admin-select-input/admin-select-input.component';
import { DynamicInputConfigType } from '../../../dynamic/types';
import type { Admin } from '../../../misc/types';
import {
  COUNTRY_CODES,
  DEGREE_TYPE_OPTIONS,
  DEGREE_TYPE_PRACTICE_ID,
  DEGREE_TYPE_TRAINING_ID,
  EDUCATION_OFFER_TYPES,
  EDUCATION_TYPE_DUAL_STUDY_ID,
  EDUCATION_TYPE_OPTIONS,
  EDUCATION_TYPE_TRAINING_ID,
  EMPLOYMENT_TYPE_TO_OFFER_TYPE_MAP,
  EMPLOYMENT_TYPE_TO_WORKING_PLAN_MAP,
  HOMEOFFICE_TYPE_OPTIONS,
  HOMEOFFICE_TYPE_PERCENTAGE_ID,
  JOB_OFFER_TYPES,
  NO_OPTION,
  OFFER_TYPE_APPRENTICESHIP_ID,
  OFFER_TYPE_OPTIONS,
  OFFER_TYPE_SELF_EMPLOYMENT_ID,
  OFFER_TYPE_WORKPLACE_ID,
  REGIONS,
  WORKING_PLAN_EDUCATION_OPTIONS,
  WORKING_PLAN_REGULAR_OPTIONS,
  YES_OPTION,
} from '../constants';
import { GfeaAddressInputComponent } from '../gfea-address-input/gfea-address-input.component';
import { GfeaDuplicateJobsComponent } from '../gfea-duplicate-jobs/gfea-duplicate-jobs.component';
import type { ProfessionSelection } from '../gfea-profession-select/gfea-profession-select.component';
import { GfeaProfessionSelectComponent } from '../gfea-profession-select/gfea-profession-select.component';
import { GfeaSalaryInputComponent } from '../gfea-salary-input/gfea-salary-input.component';
import type { Company, Offer, Posting, PostingData } from '../types';
import { ProfessionCategory } from '../types';

const TITLE_MAX_LENGTH = 100;
const DESCRIPTION_MIN_LENGTH = 30;
// They said the limit is 10000
// But the last character is turned into "...", so we need to subtract 1
const DESCRIPTION_MAX_LENGTH = 9999;

const SalaryNumberValidators = [
  Validators.min(0.01),
  Validators.max(9999999.99),
  Validators.pattern(NUMBER_WITH_SEPARATORS_PATTERN),
];

/* eslint-disable  max-lines */
// eslint-disable-next-line max-lines-per-function
export const initEmptyPostingForm = (): UntypedFormGroup =>
  new UntypedFormGroup({
    companyData: new UntypedFormGroup({
      name: new UntypedFormControl('', [
        Validators.required,
        Validators.maxLength(90),
        noWhitespaceValidator,
      ]),
      contact: new UntypedFormGroup({
        adminId: new UntypedFormControl(null, [Validators.required]),
        address: new UntypedFormGroup({
          countryCode: new UntypedFormControl('', [Validators.required]),
          postalCode: new UntypedFormControl('', [
            Validators.required,
            Validators.maxLength(15),
            noWhitespaceValidator,
          ]),
          region: new UntypedFormControl('', [Validators.required]),
          municipality: new UntypedFormControl('', [
            Validators.required,
            Validators.maxLength(36),
            noWhitespaceValidator,
          ]),
          streetName: new UntypedFormControl('', [Validators.maxLength(30), noWhitespaceValidator]),
        }),
      }),
    }),
    offerData: new UntypedFormGroup({
      title: new UntypedFormControl('', [
        Validators.required,
        Validators.maxLength(TITLE_MAX_LENGTH),
        noWhitespaceValidator,
      ]),
      titleCode: new UntypedFormControl('', []),
      titleCodeName: new UntypedFormControl('', []),
      description: new UntypedFormControl('', [
        Validators.required,
        Validators.maxLength(DESCRIPTION_MAX_LENGTH),
        Validators.minLength(DESCRIPTION_MIN_LENGTH),
        noWhitespaceValidator,
      ]),
      offerType: new UntypedFormControl('', [Validators.required]),
      miniJob: new UntypedFormControl(null, []),
      socialInsurance: new UntypedFormControl(null, []),
      internetReference: new UntypedFormControl('', [
        Validators.required,
        Validators.maxLength(254),
        noWhitespaceValidator,
      ]),
      workingPlan: new UntypedFormControl('', [Validators.required]),
      numberToFill: new UntypedFormControl(
        '1',
        Validators.compose([
          Validators.required,
          Validators.pattern(/^\d+$/),
          Validators.max(999),
          Validators.min(1),
        ]),
      ),
      supervisionDesired: new UntypedFormControl(null, []),
      educationType: new UntypedFormControl(null, []),
      degreeType: new UntypedFormControl(null, []),
      course: new UntypedFormControl('', []),
      courseName: new UntypedFormControl('', []),
      positionLocation: new UntypedFormGroup({
        countryCode: new UntypedFormControl('', [Validators.required]),
        postalCode: new UntypedFormControl('', [
          Validators.required,
          Validators.maxLength(15),
          noWhitespaceValidator,
        ]),
        region: new UntypedFormControl('', [Validators.required]),
        municipality: new UntypedFormControl('', [
          Validators.required,
          Validators.maxLength(36),
          noWhitespaceValidator,
        ]),
        streetName: new UntypedFormControl('', [Validators.maxLength(30), noWhitespaceValidator]),
      }),
      positionLocations: new UntypedFormArray([]),
      privatePlacement: new UntypedFormControl(false, []),
      labourLeasing: new UntypedFormControl(false, []),
      homeoffice: new UntypedFormGroup({
        homeofficePossible: new UntypedFormControl(false, []),
        homeofficeType: new UntypedFormControl(null, []),
        homeofficePercentage: new UntypedFormControl(null, [
          Validators.min(1),
          Validators.max(100),
          Validators.pattern(/^\d+$/),
        ]),
      }),
      salary: new UntypedFormGroup({
        compensationProfile: new UntypedFormControl(null, [Validators.required]),
        compensationType: new UntypedFormControl(null, []),
        fixedSalary: new UntypedFormControl(null, SalaryNumberValidators),
        salaryRangeFrom: new UntypedFormControl(null, SalaryNumberValidators),
        salaryRangeTo: new UntypedFormControl(null, SalaryNumberValidators),
        year1: new UntypedFormControl(null, SalaryNumberValidators),
        year2: new UntypedFormControl(null, SalaryNumberValidators),
        year3: new UntypedFormControl(null, SalaryNumberValidators),
        year4: new UntypedFormControl(null, SalaryNumberValidators),
      }),
    }),
    duplicateOfferIds: new UntypedFormControl(null, []),
  });

export const initPrefilledPostingForm = ({
  admin,
  company,
  offer,
  preparedDescription,
  prefilledLocation,
}: {
  admin: Admin;
  company: Company;
  offer: Offer;
  preparedDescription: string;
  prefilledLocation: LocationBrief;
}): UntypedFormGroup => {
  const companyRegion = REGIONS.find(region => region.name === company.stateName);
  const offerRegion = REGIONS.find(region => region.name === prefilledLocation.stateName);

  const form = initEmptyPostingForm();
  form.patchValue({
    companyData: {
      name: company.name,
      contact: {
        adminId: admin.id,
        address: {
          countryCode: COUNTRY_CODES.includes(company.countryCode!) ? company.countryCode : '',
          region: companyRegion?.vamCode || '',
          postalCode: company.postalCode || '',
          municipality: company.city || '',
          streetName: `${company.street || ''} ${company.number || ''}`.trim(),
        },
      },
    },
    offerData: {
      title: offer.title,
      description: preparedDescription,
      internetReference: offer.careersUrl,
      offerType: EMPLOYMENT_TYPE_TO_OFFER_TYPE_MAP[offer.employmentType!]?.value || '',
      workingPlan: EMPLOYMENT_TYPE_TO_WORKING_PLAN_MAP[offer.employmentType!]?.value || '',
      positionLocation: {
        countryCode: COUNTRY_CODES.includes(prefilledLocation.countryCode!)
          ? prefilledLocation.countryCode
          : '',
        region: offerRegion?.vamCode || '',
        postalCode: prefilledLocation.postalCode || '',
        municipality: prefilledLocation.city || '',
        streetName: prefilledLocation.street || '',
      },
    },
  });

  markDirtyAndTouched(form);
  return form;
};

const markDirtyAndTouched = (form: UntypedFormGroup): void => {
  const markDirtyAndTouchedRecursively = (formGroup: UntypedFormGroup) => {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control && control?.value !== '') {
        control.markAsDirty();
        control.markAsTouched();
        control.updateValueAndValidity();
      }
      if (control instanceof UntypedFormGroup) {
        markDirtyAndTouchedRecursively(control);
      }
    });
  };
  markDirtyAndTouchedRecursively(form);
};

export const initPostingForm = (posting: Posting): UntypedFormGroup => {
  const data = {
    ...posting.data,
    offerData: {
      ...posting.data.offerData,
      socialInsurance: yesNoOptionToBool(posting.data.offerData.socialInsurance),
      miniJob: yesNoOptionToBool(posting.data.offerData.miniJob),
      supervisionDesired: yesNoOptionToBool(posting.data.offerData.supervisionDesired),
      privatePlacement: yesNoOptionToBool(posting.data.offerData.privatePlacement),
      labourLeasing: yesNoOptionToBool(posting.data.offerData.labourLeasing),
      homeoffice: {
        ...posting.data.offerData.homeoffice,
        homeofficePossible: yesNoOptionToBool(
          posting.data.offerData.homeoffice?.homeofficePossible,
        ),
      },
    },
    duplicateOfferIds: posting.duplicateOfferIds.length ? posting.duplicateOfferIds : null,
  };

  const form = initEmptyPostingForm();
  form.patchValue(data);
  return form;
};

export const postingFormValue = (
  form: UntypedFormGroup,
): { data: PostingData; duplicateOfferIds: number[] } => {
  const value = form.getRawValue();
  const salary = [
    'fixedSalary',
    'salaryRangeFrom',
    'salaryRangeTo',
    'year1',
    'year2',
    'year3',
    'year4',
  ].reduce((acc, field) => {
    acc[field] = acc[field] !== null ? parseInt(acc[field], 10) : null;
    return acc;
  }, value.offerData.salary);

  const homeoffice = {
    ...value.offerData.homeoffice,
    homeofficePossible: yesNoBoolToOption(value.offerData.homeoffice.homeofficePossible),
    homeofficePercentage:
      value.offerData.homeoffice.homeofficePercentage !== null
        ? parseInt(value.offerData.homeoffice.homeofficePercentage, 10)
        : null,
  };

  return trimmedFormValue({
    data: {
      offerData: {
        ...value.offerData,
        numberToFill: parseInt(value.offerData.numberToFill, 10),
        socialInsurance: yesNoBoolToOption(value.offerData.socialInsurance),
        miniJob: yesNoBoolToOption(value.offerData.miniJob),
        supervisionDesired: yesNoBoolToOption(value.offerData.supervisionDesired),
        privatePlacement: yesNoBoolToOption(value.offerData.privatePlacement),
        labourLeasing: yesNoBoolToOption(value.offerData.labourLeasing),
        homeoffice,

        positionLocation: value.offerData.positionLocations.length
          ? undefined
          : value.offerData.positionLocation,
        salary,
      },
      companyData: value.companyData,
    },
    duplicateOfferIds: value.duplicateOfferIds || [],
  });
};

const yesNoBoolToOption = (value: boolean | null): string | null =>
  value === null ? null : !!value ? YES_OPTION : NO_OPTION;

const yesNoOptionToBool = (value: string | null): boolean | null =>
  value === YES_OPTION ? true : value === NO_OPTION ? false : null;

@Component({
  selector: 'rti-gfea-posting-form',
  templateUrl: './gfea-posting-form.component.html',
  styleUrls: ['./gfea-posting-form.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AdminSelectInputComponent,
    CheckboxComponent,
    DividerComponent,
    FormFieldComponent,
    GfeaAddressInputComponent,
    GfeaSalaryInputComponent,
    GfeaDuplicateJobsComponent,
    GfeaProfessionSelectComponent,
    HeadingComponent,
    IconHelpComponent,
    InputComponent,
    LabelComponent,
    ReactiveFormsModule,
    RtTooltip,
    SelectComponent,
    SpinnerComponent,
    TextareaComponent,
    TranslatePipe,
  ],
})
@Destroyable()
export class GfeaPostingFormComponent {
  private readonly cdRef = inject(ChangeDetectorRef);

  public readonly posting = input<Posting>();
  public readonly offer = input<Offer>(undefined!);
  @Input() public form: UntypedFormGroup;
  @Input() public selectableOffers: Offer[] | null;

  public ProfessionCategory = ProfessionCategory;

  public OFFER_TYPE_OPTIONS = OFFER_TYPE_OPTIONS;
  public WORKING_PLAN_REGULAR_OPTIONS = WORKING_PLAN_REGULAR_OPTIONS;
  public WORKING_PLAN_EDUCATION_OPTIONS = WORKING_PLAN_EDUCATION_OPTIONS;
  public EDUCATION_TYPE_OPTIONS = EDUCATION_TYPE_OPTIONS;
  public DEGREE_TYPE_OPTIONS = DEGREE_TYPE_OPTIONS;
  public HOMEOFFICE_TYPE_OPTIONS = HOMEOFFICE_TYPE_OPTIONS;
  public HOMEOFFICE_TYPE_PERCENTAGE_ID = HOMEOFFICE_TYPE_PERCENTAGE_ID;

  public TITLE_MAX_LENGTH = TITLE_MAX_LENGTH;
  public DESCRIPTION_MIN_LENGTH = DESCRIPTION_MIN_LENGTH;
  public DESCRIPTION_MAX_LENGTH = DESCRIPTION_MAX_LENGTH;

  public OFFER_TYPE_APPRENTICESHIP_ID = OFFER_TYPE_APPRENTICESHIP_ID;

  public workingPlanSelectOptions: SelectOption[] = [];
  public titleCodeSelection: ProfessionSelection | null = null;
  public courseSelection: ProfessionSelection | null = null;
  public contactAdminIdFormInput: AdminSelectInput;

  public get isInEditMode(): boolean {
    return !!this.posting();
  }
  // eslint-disable-next-line max-lines-per-function
  public ngOnInit(): void {
    this.initContactAdminSelect(this.form.value.companyData.contact.adminId);
    this.setTitleCodeSelection(this.form.value.offerData);
    this.setCourseSelection(this.form.value.offerData);
    this.setTitleCodeControlSettings(
      this.form.value.offerData.offerType,
      this.form.value.offerData.educationType,
      this.form.value.offerData.degreeType,
    );
    this.setCourseControlSettings(
      this.form.value.offerData.offerType,
      this.form.value.offerData.educationType,
      this.form.value.offerData.degreeType,
    );

    this.setMiniJobAndSocialInsuraceControlSettings(this.form.value.offerData.offerType);
    this.setPositionLocationGroupSettings(this.form.value.duplicateOfferIds);
    this.setEducationTypeControlSettings(this.form.value.offerData.offerType);
    this.setDegreeTypeControlSettings(this.form.value.offerData.educationType);
    this.setWorkingPlanSelectOptions(this.form.value.offerData.offerType);
    this.setOfferTypeSettings();
    this.setHomeofficeTypeControlSettings(this.form.value.offerData.homeoffice?.homeofficePossible);
    this.setHomeofficePercentageControlSettings(
      this.form.value.offerData.homeoffice?.homeofficeType,
    );

    const homeofficeGroup = <UntypedFormGroup>(
      (<UntypedFormGroup>this.form.controls.offerData).controls.homeoffice
    );

    homeofficeGroup.controls.homeofficePossible.valueChanges
      .pipe(takeUntilDestroyed(this))
      .subscribe(value => {
        this.setHomeofficeTypeControlSettings(value);
      });

    homeofficeGroup.controls.homeofficeType.valueChanges
      .pipe(takeUntilDestroyed(this))
      .subscribe(value => {
        this.setHomeofficePercentageControlSettings(value);
      });

    this.form.controls.offerData.valueChanges.pipe(takeUntilDestroyed(this)).subscribe(changes => {
      if ('titleCode' in changes && 'titleCodeName' in changes) {
        this.setTitleCodeSelection(changes);
      }
      if ('course' in changes && 'courseName' in changes) {
        this.setCourseSelection(changes);
      }
    });

    this.form.controls.duplicateOfferIds.valueChanges
      .pipe(takeUntilDestroyed(this))
      .subscribe(changes => {
        this.setPositionLocationGroupSettings(changes);
      });

    (<UntypedFormGroup>this.form.controls.offerData).controls.offerType.valueChanges
      .pipe(takeUntilDestroyed(this))
      .subscribe(changes => {
        this.setMiniJobAndSocialInsuraceControlSettings(changes);
        this.setMiniJobAndSocialInsuranceDefaultValue(changes);
        this.setEducationTypeControlSettings(changes);
        this.titleCodeSelectionChanged(null);
        this.courseSelectionChanged(null);
        this.setTitleCodeControlSettings(
          changes,
          this.form.value.offerData.educationType,
          this.form.value.offerData.degreeType,
        );
        this.setCourseControlSettings(
          changes,
          this.form.value.offerData.educationType,
          this.form.value.offerData.degreeType,
        );
        this.setWorkingPlanSelectOptions(changes);
      });

    (<UntypedFormGroup>this.form.controls.offerData).controls.educationType.valueChanges
      .pipe(takeUntilDestroyed(this))
      .subscribe(changes => {
        this.setDegreeTypeControlSettings(changes);
        this.setTitleCodeControlSettings(
          this.form.value.offerData.offerType,
          changes,
          this.form.value.offerData.degreeType,
        );
        this.setCourseControlSettings(
          this.form.value.offerData.offerType,
          changes,
          this.form.value.offerData.degreeType,
        );
      });

    (<UntypedFormGroup>this.form.controls.offerData).controls.degreeType.valueChanges
      .pipe(takeUntilDestroyed(this))
      .subscribe(changes => {
        this.setTitleCodeControlSettings(
          this.form.value.offerData.offerType,
          this.form.value.offerData.educationType,
          changes,
        );
        this.setCourseControlSettings(
          this.form.value.offerData.offerType,
          this.form.value.offerData.educationType,
          changes,
        );
      });
  }

  public titleCodeSelectionChanged(selection: ProfessionSelection | null): void {
    this.form.markAsDirty();
    if (selection) {
      this.form.controls.offerData.patchValue({
        titleCode: selection.id,
        titleCodeName: selection.value,
      });
    } else {
      this.form.controls.offerData.patchValue({ titleCode: '', titleCodeName: '' });
    }
  }

  public courseSelectionChanged(selection: ProfessionSelection | null): void {
    this.form.markAsDirty();
    if (selection) {
      this.form.controls.offerData.patchValue({
        course: selection.id,
        courseName: selection.value,
      });
    } else {
      this.form.controls.offerData.patchValue({ course: '', courseName: '' });
    }
  }

  public get titleCodeProfessionCategory(): ProfessionCategory | null {
    const currentOfferType = this.form.value.offerData.offerType;
    const currentEducationType = this.form.value.offerData.educationType;
    const currentDegreeType = this.form.value.offerData.degreeType;

    if (JOB_OFFER_TYPES.includes(currentOfferType)) {
      return ProfessionCategory.WORK;
    } else if (
      EDUCATION_OFFER_TYPES.includes(currentOfferType) &&
      currentEducationType === EDUCATION_TYPE_TRAINING_ID
    ) {
      return ProfessionCategory.EDUCATION;
    } else if (
      EDUCATION_OFFER_TYPES.includes(currentOfferType) &&
      currentEducationType === EDUCATION_TYPE_DUAL_STUDY_ID &&
      currentDegreeType === DEGREE_TYPE_TRAINING_ID
    ) {
      return ProfessionCategory.TRAINING_INTEGRATED;
    } else {
      return null;
    }
  }

  public get courseProfessionCategory(): ProfessionCategory | null {
    const currentOfferType = this.form.value.offerData.offerType;
    const currentEducationType = this.form.value.offerData.educationType;
    const currentDegreeType = this.form.value.offerData.degreeType;

    if (
      currentOfferType === OFFER_TYPE_APPRENTICESHIP_ID &&
      currentEducationType === EDUCATION_TYPE_DUAL_STUDY_ID &&
      !!currentDegreeType
    ) {
      return ProfessionCategory.STUDIES;
    } else {
      return null;
    }
  }

  public toggleSocialInsuranceAndMiniJob(): void {
    const {
      controls: { socialInsurance: socialInsuranceControl, miniJob: miniJobControl },
    } = <UntypedFormGroup>this.form.controls.offerData;

    if (!socialInsuranceControl.disabled) {
      socialInsuranceControl.setValue(!socialInsuranceControl.value);
      this.markControlAsTouchedAndDirty(socialInsuranceControl);
    }
    if (!miniJobControl.disabled) {
      miniJobControl.setValue(!miniJobControl.value);
      this.markControlAsTouchedAndDirty(miniJobControl);
    }

    this.cdRef.detectChanges();
  }

  public toggleSupervisionDesired(): void {
    const {
      controls: { supervisionDesired: supervisionDesiredControl },
    } = <UntypedFormGroup>this.form.controls.offerData;

    this.markControlAsTouchedAndDirty(<AbstractControl>supervisionDesiredControl);
    supervisionDesiredControl.setValue(!supervisionDesiredControl.value);

    this.cdRef.detectChanges();
  }

  public togglePrivatePlacement(): void {
    const {
      controls: { privatePlacement: privatePlacementControl, labourLeasing: labourLeasingControl },
    } = <UntypedFormGroup>this.form.controls.offerData;

    this.markControlAsTouchedAndDirty(<AbstractControl>privatePlacementControl);
    privatePlacementControl.setValue(!privatePlacementControl.value);

    if (privatePlacementControl.value && labourLeasingControl.value) {
      this.markControlAsTouchedAndDirty(<AbstractControl>labourLeasingControl);
      labourLeasingControl.setValue(false);
    }

    this.cdRef.detectChanges();
  }

  public toggleLabourLeasing(): void {
    const {
      controls: { privatePlacement: privatePlacementControl, labourLeasing: labourLeasingControl },
    } = <UntypedFormGroup>this.form.controls.offerData;

    this.markControlAsTouchedAndDirty(<AbstractControl>labourLeasingControl);
    labourLeasingControl.setValue(!labourLeasingControl.value);

    if (privatePlacementControl.value && labourLeasingControl.value) {
      this.markControlAsTouchedAndDirty(<AbstractControl>privatePlacementControl);
      privatePlacementControl.setValue(false);
    }

    this.cdRef.detectChanges();
  }

  public toggleHomeofficePossible(): void {
    const homeofficePossibleControl = this.form.get('offerData.homeoffice.homeofficePossible')!;
    this.markControlAsTouchedAndDirty(homeofficePossibleControl);
    homeofficePossibleControl.setValue(!homeofficePossibleControl.value);
    this.cdRef.detectChanges();
  }

  public educationOfferSelected(): boolean {
    return EDUCATION_OFFER_TYPES.includes(this.form.value.offerData.offerType);
  }

  private setTitleCodeSelection(data: { titleCode: string; titleCodeName: string }): void {
    this.titleCodeSelection =
      data.titleCode && data.titleCodeName
        ? { id: data.titleCode, value: data.titleCodeName }
        : null;
    this.cdRef.detectChanges();
  }

  private setCourseSelection(data: { course: string; courseName: string }): void {
    this.courseSelection =
      data.course && data.courseName ? { id: data.course, value: data.courseName } : null;
    this.cdRef.detectChanges();
  }

  private setMiniJobAndSocialInsuraceControlSettings(offerType: string | null): void {
    const {
      controls: { socialInsurance: socialInsuranceControl, miniJob: miniJobControl },
    } = <UntypedFormGroup>this.form.controls.offerData;

    if (!offerType) {
      this.disableControl(miniJobControl);
      this.disableControl(socialInsuranceControl);
    } else if (offerType === OFFER_TYPE_WORKPLACE_ID) {
      this.enableControl(miniJobControl);
      this.enableControl(socialInsuranceControl);
    } else if (offerType === OFFER_TYPE_SELF_EMPLOYMENT_ID) {
      this.disableControl(miniJobControl);
      this.disableControl(socialInsuranceControl);
    } else {
      this.disableControl(miniJobControl);
      this.enableControl(socialInsuranceControl);
    }

    this.cdRef.detectChanges();
  }

  private setPositionLocationGroupSettings(duplicateOfferIds: number[] | null): void {
    const {
      controls: { positionLocation: positionLocationGroup },
    } = <UntypedFormGroup>this.form.controls.offerData;

    if (duplicateOfferIds?.length) {
      positionLocationGroup.disable({ emitEvent: false });
    } else {
      positionLocationGroup.enable({ emitEvent: false });
    }

    positionLocationGroup.updateValueAndValidity();
    this.cdRef.detectChanges();
  }

  private setMiniJobAndSocialInsuranceDefaultValue(offerType: string | null): void {
    if (!offerType) return;

    const {
      controls: { socialInsurance: socialInsuranceControl, miniJob: miniJobControl },
    } = <UntypedFormGroup>this.form.controls.offerData;

    if (offerType !== OFFER_TYPE_SELF_EMPLOYMENT_ID) {
      socialInsuranceControl.setValue(true, { emitEvent: false });
    }
    if (offerType === OFFER_TYPE_WORKPLACE_ID) {
      miniJobControl.setValue(false, { emitEvent: false });
    }

    this.cdRef.detectChanges();
  }

  private setOfferTypeSettings(): void {
    if (this.isInEditMode) {
      this.form.controls.offerData.get('offerType')?.disable();
      this.cdRef.detectChanges();
    }
  }

  private setEducationTypeControlSettings(offerType: string | null): void {
    const {
      controls: { educationType: educationTypeControl, degreeType: degreeTypeControl },
    } = <UntypedFormGroup>this.form.controls.offerData;

    if (this.isInEditMode) {
      educationTypeControl.disable();
      degreeTypeControl.disable();
      this.cdRef.detectChanges();
      return;
    }

    if (offerType === OFFER_TYPE_APPRENTICESHIP_ID) {
      this.enableControl(educationTypeControl);
    } else {
      this.disableControl(educationTypeControl);
      this.disableControl(degreeTypeControl);
    }

    this.cdRef.detectChanges();
  }

  private setDegreeTypeControlSettings(educationType: string | null): void {
    const {
      controls: { degreeType: degreeTypeControl },
    } = <UntypedFormGroup>this.form.controls.offerData;

    if (educationType === EDUCATION_TYPE_DUAL_STUDY_ID) {
      this.enableControl(degreeTypeControl);
    } else {
      this.disableControl(degreeTypeControl);
    }

    this.cdRef.detectChanges();
  }

  private disableControl(control: AbstractControl): void {
    control.clearValidators();
    control.disable({ emitEvent: false });
    control.setValue(null, { emitEvent: false });
    control.updateValueAndValidity();
  }

  private enableControl(control: AbstractControl): void {
    control.setValidators([Validators.required]);
    control.enable({ emitEvent: false });
    control.updateValueAndValidity();
  }

  private initContactAdminSelect(adminId: number | null): void {
    this.contactAdminIdFormInput = new AdminSelectInput({
      type: DynamicInputConfigType.ADMIN_SELECT,
      name: 'adminId',
      required: true,
      value: adminId!,
    });

    this.contactAdminIdFormInput.control.valueChanges.subscribe(value => {
      (<any>this.form).controls.companyData.controls.contact.controls.adminId.setValue(value);
      this.form.markAsDirty();
    });
  }

  private setTitleCodeControlSettings(
    offerType: string | null,
    educationType: string | null,
    degreeType: string | null,
  ): void {
    const {
      controls: { titleCode: titleCodeControl, titleCodeName: titleCodeNameControl },
    } = <UntypedFormGroup>this.form.controls.offerData;

    if (
      offerType === OFFER_TYPE_APPRENTICESHIP_ID &&
      educationType === EDUCATION_TYPE_DUAL_STUDY_ID &&
      degreeType === DEGREE_TYPE_PRACTICE_ID
    ) {
      this.titleCodeSelectionChanged(null);
      this.disableControl(titleCodeControl);
      this.disableControl(titleCodeNameControl);
    } else {
      this.enableControl(titleCodeControl);
      this.enableControl(titleCodeNameControl);
    }

    this.cdRef.detectChanges();
  }

  private markControlAsTouchedAndDirty(control: AbstractControl): void {
    control.markAsDirty();
    control.markAsTouched();
  }

  private setCourseControlSettings(
    offerType: string | null,
    educationType: string | null,
    degreeType: string | null,
  ): void {
    const {
      controls: { course: courseControl, courseName: courseNameControl },
    } = <UntypedFormGroup>this.form.controls.offerData;

    if (
      offerType === OFFER_TYPE_APPRENTICESHIP_ID &&
      educationType === EDUCATION_TYPE_DUAL_STUDY_ID &&
      degreeType
    ) {
      this.enableControl(courseControl);
      this.enableControl(courseNameControl);
    } else {
      this.courseSelectionChanged(null);
      this.disableControl(courseControl);
      this.disableControl(courseNameControl);
    }

    this.cdRef.detectChanges();
  }

  private setHomeofficeTypeControlSettings(homeofficePossible: boolean | null): void {
    const homeofficeTypeControl = this.form.get('offerData.homeoffice.homeofficeType')!;
    if (homeofficePossible === true) {
      this.enableControl(homeofficeTypeControl);
    } else {
      this.disableControl(homeofficeTypeControl);
      this.disableControl(this.form.get('offerData.homeoffice.homeofficePercentage')!);
    }
    this.cdRef.detectChanges();
  }

  private setHomeofficePercentageControlSettings(homeofficeType: string | null): void {
    const percentageControl = this.form.get('offerData.homeoffice.homeofficePercentage')!;
    if (homeofficeType === HOMEOFFICE_TYPE_PERCENTAGE_ID) {
      percentageControl.setValidators([
        Validators.required,
        Validators.min(1),
        Validators.max(100),
        Validators.pattern(/^\d+$/),
      ]);
      percentageControl.enable({ emitEvent: false });
    } else {
      this.disableControl(percentageControl);
    }
    percentageControl.updateValueAndValidity();
    this.cdRef.detectChanges();
  }

  private setWorkingPlanSelectOptions(offerType: string | null): void {
    const {
      controls: { workingPlan: workingPlanControl },
    } = <UntypedFormGroup>this.form.controls.offerData;
    if (!offerType) {
      this.workingPlanSelectOptions = [];
    } else if (offerType === OFFER_TYPE_APPRENTICESHIP_ID) {
      this.workingPlanSelectOptions = WORKING_PLAN_EDUCATION_OPTIONS;
    } else {
      this.workingPlanSelectOptions = WORKING_PLAN_REGULAR_OPTIONS;
    }

    const validValues = this.workingPlanSelectOptions.map(option => option.value);

    if (!validValues.includes(workingPlanControl.value)) workingPlanControl.setValue(null);

    this.cdRef.detectChanges();
  }
}
